import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import PageTopBar from "../../../components/common/PageTopBar";
import { toolbarButtonSx, toolbarFieldsSx } from "../../../components/common/toolbarStyles";
import { useNotify } from "../../../context/NotificationContext";
import { UnsavedChangesBar } from "../../settings/components/UnsavedChangesBar";
import { colorOf } from "../../academicOperations/components/timetable/timetableUi";
import { HALF_LABELS } from "../../leave/leaveFormat";
import { useSubstitutionDay } from "../hooks/useSubstitutionDay";
import { SubstitutionNeed } from "../types";

const headCellSx = { fontWeight: 600, py: 1, bgcolor: "grey.50", whiteSpace: "nowrap" };
const NOT_COVERED = "";

/**
 * Staff → Substitutions (admin): pick a date, see every period whose faculty is on approved leave,
 * and choose by hand who covers each one. Only free faculty are offered (not teaching then, not on
 * leave, not covering another class then), same subject first, then fewest covers this week. A
 * faculty picked for one class is disabled for other classes in the same period. Saved together.
 */
export default function SubstitutionsPage() {
  const notify = useNotify();
  const { date, pendingDate, day, draft, changedSlots, dirty, loading, saving, error, saveError, actions } = useSubstitutionDay();
  const [uncoveredOnly, setUncoveredOnly] = useState(false);

  // warn before closing/reloading the tab with unsaved picks
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const needs = useMemo(() => day?.needs ?? [], [day]);
  const uncovered = needs.filter((need) => draft[need.timetableSlotId] == null).length;
  const visible = uncoveredOnly ? needs.filter((need) => draft[need.timetableSlotId] == null) : needs;
  const current = dayjs(date);

  /** Who covers another class in the same period (in the picks on screen), by faculty id. */
  const takenInPeriod = (need: SubstitutionNeed) => {
    const taken = new Map<number, string>();
    needs
      .filter((other) => other.periodId === need.periodId && other.timetableSlotId !== need.timetableSlotId)
      .forEach((other) => {
        const id = draft[other.timetableSlotId];
        if (id != null) taken.set(id, other.className);
      });
    return taken;
  };

  const handleSave = async () => {
    if (await actions.save()) notify("Substitutions saved.", "success");
  };

  const dateBar = (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={toolbarFieldsSx}>
      <Tooltip title="Previous day">
        <IconButton size="small" onClick={() => actions.selectDate(current.subtract(1, "day").format("YYYY-MM-DD"))}>
          <ChevronLeftIcon />
        </IconButton>
      </Tooltip>
      <DatePicker
        value={current}
        onChange={(value) => value?.isValid() && actions.selectDate(value.format("YYYY-MM-DD"))}
        format="ddd, DD MMM YYYY"
        slotProps={{ textField: ({ isFieldRequired, ...params }: any) => ({ ...params, size: "small", sx: { width: 190 } }) }}
      />
      <Tooltip title="Next day">
        <IconButton size="small" onClick={() => actions.selectDate(current.add(1, "day").format("YYYY-MM-DD"))}>
          <ChevronRightIcon />
        </IconButton>
      </Tooltip>
      <Button
        size="small"
        variant="outlined"
        sx={{ ...toolbarButtonSx, textTransform: "none" }}
        disabled={current.isSame(dayjs(), "day")}
        onClick={() => actions.selectDate(dayjs().format("YYYY-MM-DD"))}
      >
        Today
      </Button>
    </Stack>
  );

  const renderPicker = (need: SubstitutionNeed) => {
    const value = draft[need.timetableSlotId];
    const taken = takenInPeriod(need);
    const known = need.candidates.some((c) => c.facultyId === value);
    const changed = changedSlots.includes(need.timetableSlotId);

    if (need.candidates.length === 0 && value == null) {
      return (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: "warning.dark" }}>
          <WarningAmberRoundedIcon fontSize="small" />
          <Typography variant="body2" fontWeight={600}>
            No one is free
          </Typography>
        </Stack>
      );
    }

    return (
      <FormControl size="small" fullWidth sx={{ minWidth: 220 }}>
        <Select
          value={value == null ? NOT_COVERED : String(value)}
          onChange={(event) => actions.pick(need.timetableSlotId, event.target.value === NOT_COVERED ? null : Number(event.target.value))}
          disabled={saving}
          displayEmpty
          inputProps={{ "aria-label": `Substitute for ${need.periodName} ${need.className}` }}
          renderValue={(selected) => {
            if (selected === NOT_COVERED)
              return (
                <Typography variant="body2" color="warning.dark" fontWeight={600}>
                  Not covered
                </Typography>
              );
            const candidate = need.candidates.find((c) => String(c.facultyId) === selected);
            return (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight={600}>
                  {candidate?.name ?? "Assigned (no longer free)"}
                </Typography>
                {candidate?.sameSubject && <Chip label="Same subject" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
              </Stack>
            );
          }}
          sx={{
            fontSize: 14,
            bgcolor: (t) => (changed ? alpha(t.palette.warning.light, 0.15) : value == null ? alpha(t.palette.warning.light, 0.06) : undefined),
          }}
        >
          <MenuItem value={NOT_COVERED}>
            <ListItemText primary="Not covered" slotProps={{ primary: { color: "text.secondary", fontSize: 14 } }} />
          </MenuItem>
          {!known && value != null && (
            <MenuItem value={String(value)} disabled>
              <ListItemText primary="Assigned (no longer free)" />
            </MenuItem>
          )}
          {need.candidates.map((candidate) => {
            const busyIn = taken.get(candidate.facultyId);
            return (
              <MenuItem key={candidate.facultyId} value={String(candidate.facultyId)} disabled={Boolean(busyIn)}>
                <ListItemText
                  primary={candidate.name}
                  secondary={
                    busyIn
                      ? `Covering ${busyIn} then`
                      : `${candidate.sameSubject ? "Teaches " + need.subjectName + " · " : ""}${candidate.coversThisWeek} cover${candidate.coversThisWeek === 1 ? "" : "s"} this week`
                  }
                  slotProps={{ primary: { fontSize: 14, fontWeight: 600 }, secondary: { fontSize: 12 } }}
                />
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    );
  };

  const renderBody = () => {
    if (loading && !day) {
      return (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (error) return <Alert severity="error">{error}</Alert>;
    if (needs.length === 0) {
      return (
        <Paper variant="outlined" sx={{ borderRadius: 2, py: 7, px: 2, textAlign: "center" }}>
          <EventAvailableOutlinedIcon sx={{ fontSize: 40, color: "success.light", mb: 1 }} />
          <Typography fontWeight={600}>Nothing to cover on {current.format("ddd, D MMM")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {day?.academicYear ? "No faculty with timetable periods is on approved leave that day." : "The date is outside the active academic year."}
          </Typography>
        </Paper>
      );
    }
    return (
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, opacity: loading ? 0.6 : 1, transition: "opacity 150ms" }}>
        <Table size="small" sx={{ minWidth: 820 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Period</TableCell>
              <TableCell sx={headCellSx}>Class</TableCell>
              <TableCell sx={headCellSx}>Subject</TableCell>
              <TableCell sx={headCellSx}>On leave</TableCell>
              <TableCell sx={{ ...headCellSx, width: 300 }}>Substitute</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Every period is covered.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              visible.map((need) => (
                <TableRow key={need.timetableSlotId} hover sx={{ "&:last-child td": { border: 0 }, "& td": { py: 1 } }}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="body2" fontWeight={600}>
                      {need.periodName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {need.startTime}–{need.endTime}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{need.className}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: colorOf(need.subjectCode) }} />
                      {need.subjectName}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{need.absentFacultyName}</Typography>
                    {need.halfDay && (
                      <Typography variant="caption" color="text.secondary">
                        {HALF_LABELS[need.halfDay]}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{renderPicker(need)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ mt: 2, mr: 2 }}>
      <PageTopBar
        left={
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h6" fontWeight={600}>
              Substitutions
            </Typography>
            {dateBar}
          </Stack>
        }
        right={
          needs.length > 0 && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Chip
                size="small"
                color={uncovered > 0 ? "warning" : "success"}
                label={uncovered > 0 ? `${uncovered} of ${needs.length} not covered` : `All ${needs.length} covered`}
                sx={{ fontWeight: 600 }}
              />
              <FormControlLabel
                control={<Switch size="small" checked={uncoveredOnly} onChange={(e) => setUncoveredOnly(e.target.checked)} />}
                label={<Typography variant="body2">Not covered only</Typography>}
                sx={{ mr: 0 }}
              />
            </Stack>
          )
        }
      />

      {renderBody()}

      {/* changing date with unsaved picks */}
      <Dialog open={pendingDate !== null} onClose={actions.cancelDate} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Discard changes for {current.format("ddd, D MMM")}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {changedSlots.length} pick{changedSlots.length === 1 ? "" : "s"} not saved. Save them first, or discard them and open{" "}
            {pendingDate && dayjs(pendingDate).format("ddd, D MMM")}.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={actions.cancelDate} sx={{ textTransform: "none" }}>
            Keep editing
          </Button>
          <Button color="error" onClick={actions.confirmDate} sx={{ textTransform: "none", fontWeight: 600 }}>
            Discard and switch
          </Button>
        </DialogActions>
      </Dialog>

      {/* last child of the page box, so it sticks to the bottom of the screen while scrolling */}
      <Box sx={{ height: 16 }} />
      <UnsavedChangesBar open={dirty} saving={saving} error={saveError} onDiscard={actions.discard} onSave={handleSave} />
    </Box>
  );
}
